import { del, get, patch, post, postFile } from "./requests";

const mapCompanyFields = (apiData) => {
  if (!apiData) return null;
  return {
    ...apiData,
    score: apiData.score,
    is_complete: apiData.score !== null,
    last_tax_declaration_date: apiData.last_tax_declaration_date,
    last_transaction_date: apiData.last_transaction_date,
  };
};

const apiPath = "api/";
const endPoints = {
  user: apiPath + "user",
  company: apiPath + "company",
  companies: apiPath + "clients",
  banking: apiPath + "banking",
  invitations: apiPath + "invitations"
};

export const api = {
  /**
   * Updates user profile information.
   * @param {Object} data - payload
   * @param {string} [data.phone] - phone number
   * @param {string} [data.language] - language
   */
  editUser(data) {
    return patch(endPoints.user, data);
  },

  searchUserCompany(key) {
    return post(endPoints.company + "/search", { key });
  },

  createUserCompany(newCompany) {
    return post(endPoints.company, newCompany);
  },

  getUserCompany() {
    return get(endPoints.company);
  },

  getInvitations() {
    return get(endPoints.invitations);
  },

  deleteInvitation(key) {
    return del([endPoints.invitations, key].join("/"));
  },

  getInvitation(key) {
    return get([endPoints.invitations, key].join("/"));
  },

  createInvitation(invitation) {
    return post(endPoints.invitations, invitation);
  },

  // Detect company by SIREN
  detectCompany: async (siren) => {
    const response = await post(`${endPoints.companies}/search`, {
      key: siren,
    });
    const data = await response.json();
    return mapCompanyFields(data[0]);
  },

  getCompanies: async () => {
    const response = await get(endPoints.companies);
    const data = await response.json();
    return {
      results: (data || []).map(mapCompanyFields),
      count: data?.length || 0,
    };
  },

  getCompany: async (id) => {
    const response = await get(`${endPoints.companies}/${id}`);
    const data = await response.json();
    return mapCompanyFields(data);
  },

  // Create new company
  createCompany: async (companyData) => {
    const payload = {
      ...companyData,
      identifier: companyData.identifier || companyData.identifier,
    };
    const response = await post(endPoints.companies, payload);
    const data = await response.json();
    return mapCompanyFields(data);
  },

  // not yet provided by backend
  updateCompany: async (companyData) => {
    const { id, ...data } = companyData;
    if (data.identifier) data.identifier = data.identifier;
    const response = await patch(`${endPoints.companies}/${id}`, data);
    const result = await response.json();
    return mapCompanyFields(result);
  },

  deleteCompany: async (id) => {
    await del(`${endPoints.companies}/${id}`);
  },

  searchCompanies: async (query) => {
    const response = await post(`${endPoints.companies}/search`, {
      name: query,
    });
    const data = await response.json();
    return {
      results: (data || []).map(mapCompanyFields),
      count: data?.length || 0,
    };
  },

  getStatements: async (id) => {
    const response = await get(`${endPoints.banking}/${id}/statements`);
    return await response.json();
  },

  /**
   * Create bank statements for a client company
   * @param {number} clientID - The ID of the client
   * @param {FormData} formData - FormData containing the files
   * @returns {Promise<Response>}  Server response
   */
  createStatement(clientID, formData) {
    return postFile(`${endPoints.banking}/${clientID}/statements`, formData);
  },

  /**
   * Delete bank statement
   * @param {Number} statementID statement id
   * @param {Number} clientID client company id
   * @returns {Promise<Response>}  Server response
   */
  deleteStatement(statementID, clientID) {
    return del(
      [endPoints.banking, clientID, "statements", statementID].join("/"),
    );
  },

  // Get tax clearances
  getTaxDeclarations: async (clientID) => {
    const response = await get(
      `${endPoints.banking}/${clientID}/tax-declarations`,
    );
    return await response.json();
  },

  /**
   * Create tax declaration
   * @param {String|Number} clientID client company id
   * @param {File} file tax declaration file
   * @param {String} year tax declaration year
   * @returns
   */
  createTaxDeclaration(clientID, file, year) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("year", year);
    return postFile(
      [endPoints.banking, clientID, "tax-declarations"].join("/"),
      formData,
    );
  },

  getBanks: async (companyId) => {
    try {
      const response = await get("api/banking/accounts");
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching banks:", error);
      throw error;
    }
  },

  getBankTransactions: async (
    companyId,
    bankAccountId = null,
    descriptionFilter = "",
  ) => {
    try {
      const params = new URLSearchParams();
      if (descriptionFilter) params.append("search", descriptionFilter);
      if (bankAccountId)
        params.append("bank_account_id", bankAccountId.toString());

      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await get(`api/banking/transactions${queryString}`);
      return await response.json();
    } catch (error) {
      console.error(`Error fetching transactions:`, error);
      throw error;
    }
  },

  getBankTransactions2(clientID, accountID = null, searchTerm = "") {
    const baseUrl = [endPoints.banking, clientID, "transactions"].join("/");
    const params = new URLSearchParams();

    if (accountID) {
      params.append("account_id", accountID);
    }
    
    if (searchTerm) {
      params.append("search", searchTerm);
    }

    const queryString = params.toString() ? `?${params.toString()}` : "";
    
    return get(baseUrl + queryString);
  },

  getBankCashFlow: async (companyId, bankAccountId = null) => {
    try {
      if (bankAccountId) {
        const response = await get(`api/banking/accounts/${bankAccountId}`);
        const data = await response.json();
        return data.cash_flow;
      }
      const response = await get("api/banking/statements");
      return await response.json();
    } catch (error) {
      console.error(
        `Error fetching cash flow for company ${companyId}:`,
        error,
      );
      throw error;
    }
  },

  getBanks: async (clientID) => {
    const response = await get(`${endPoints.banking}/${clientID}/accounts`);
    return await response.json();
  },

  deleteBankAccount(clientID, bankAccountID) {
    return del(
      [endPoints.banking, clientID, "accounts", bankAccountID].join("/"),
    );
  },

  deleteTaxDeclaration(clientId, taxDeclarationID) {
    return del([endPoints.banking, clientId, "tax-declarations", taxDeclarationID].join("/"));
  }
};
